import {
  IDomainFederation,
  IOrganization,
  systemRights,
} from "@tedivo/tvd-api-models";
import {
  IFields,
  IFormReturn,
  TedivoForm,
  translateTedivoForm,
} from "@tedivo/tedivo-form";
import { IOptionsStorage, ITable } from "@tedivo/tedivo-smart-table";

import { ListComponentWithActions } from "../../common/ListComponentWithActions";
import Services from "../../../app/services";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import { createTableUserStorageInstance } from "../../../app/preferences/createTableUserStorageInstance";
import { getTranslation } from "../../../app/i18/i18tn";
import goSquared from "../../../app/tracking/goSquared";
import securityModule from "../../../app/security/SecurityModule";
import { setAppTitle } from "../../../app/setAppTitle";
import { z } from "zod";

export class TVDAdminDomainFedsComponent extends ListComponentWithActions<IDomainFederation> {
  public static observedAttributes = [];

  allOrgsList: IOrganization[] = [];
  domainsConsumers: Record<string, string[]> = {};

  constructor() {
    super();

    this.h1Label = "general:organizations.federations.title";
    this.goSquaredLabel = "Admin/DomainFederations";
    this.topLabel = "general:adminArea";
    //this.createLabel = "general:parentOrganizations.adminCreate";
    this.editLabel = "general:organizations.federations.edit";
    this.deleteLabel = "general:organizations.federations .delete";

    this.serviceListCall = Services.domainFederations.getAll;
    this.serviceDeleteCall = Services.domainFederations.delete;
    this.serviceEditCall = Services.domainFederations.update;

    this.systemRightToEdit = systemRights.ORG.AppAdminDomains;

    this.preDisplayFnCall = async () => {
      const res = await Services.organizations.adminGetAllOrganizations();
      if (res.data) {
        this.allOrgsList = res.data.sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        this.allOrgsList.forEach((org) => {
          const domains = org.domains || [];
          domains.forEach((domain) => {
            if (!this.domainsConsumers[domain]) {
              this.domainsConsumers[domain] = [];
            }
            this.domainsConsumers[domain].push(org.organizationId);
          });
        });
      }
    };

    const userOptionsStorage =
      createTableUserStorageInstance<IDomainFederation>(
        "adminFederationstList",
      );

    this.tableConfig = (data: IDomainFederation[]) =>
      createParentOrgsTableConfig(
        data,
        this.systemRightToEdit,
        userOptionsStorage,
        this.allOrgsList,
        this.domainsConsumers,
        this.openEditModal,
        this.openDeleteModal,
      );

    this.createEditForm = (
      submitButton: SlButton,
      prevData?: IDomainFederation,
    ) =>
      createParentOrgEdit(
        submitButton,
        prevData,
        this.allOrgsList,
        this.domainsConsumers,
      );

    setAppTitle(getTranslation("general:parentOrganizations.title"));
    goSquared.trackPage("TEDIVO Admin Pages - Parent Organizations");
  }
}

customElements.define(
  "tvd-admin-federations-component",
  TVDAdminDomainFedsComponent,
);

declare global {
  interface HTMLElementTagNameMap {
    "tvd-admin-federations-component": TVDAdminDomainFedsComponent;
  }
}

function createParentOrgsTableConfig(
  parentOrganizations: IDomainFederation[],
  systemRightToEdit: string | undefined,
  userOptionsStorage: IOptionsStorage<IDomainFederation>,
  allOrgsList: IOrganization[],
  domainsConsumers: Record<string, string[]>,
  onEditSaveFn: (pk: string, data: IDomainFederation) => void,
  onDeleteFn: (
    pk: string,
    data: IDomainFederation,
    name: keyof IDomainFederation,
  ) => void,
): ITable<IDomainFederation> {
  const tableConfig: ITable<IDomainFederation> = {
    className: "tvd-table tvd-table-smart-table",
    data: parentOrganizations,
    defaultSortBy: "name",
    fields: [
      {
        name: "domain",
        label: getTranslation("general:domains.domain"),
        type: "string",
        fixedWidth: "240px",
      },
      {
        name: "domainOwnershipVerified",
        type: "string",
        valueFunction: (dta) =>
          dta.domainOwnershipVerified
            ? getTranslation("general:common.yes")
            : getTranslation("general:common.no"),
        className: (dta) =>
          `centered ${dta?.domainOwnershipVerified ? "text-success" : ""}`,
        fixedWidth: "160px",
        label: getTranslation(
          "general:organizations.federations.domainOwnershipVerified",
        ),
      },
      {
        name: "enabled",
        type: "string",
        className: "centered",
        fixedWidth: "200px",
        valueFunction: (dta) =>
          dta.enabled
            ? getTranslation("general:common.yes")
            : getTranslation("general:common.no"),
        label: getTranslation("general:organizations.federations.enabled"),
      },
      {
        name: "provider",
        type: "string",
        className: "centered",
        fixedWidth: "140px",
        valueFunction: (dta) =>
          dta.domainOwnershipVerified && dta.enabled
            ? dta.provider || "-"
            : "-",
        label: getTranslation("general:organizations.federations.provider"),
      },
      {
        name: "id",
        type: "string",
        valueFunction: (dta) =>
          returnListOfOrganizationsUsingDomain(dta.domain, dta.orgIdOwner).join(
            ", ",
          ) || "-",
        className: "underlned-is-highlight",
        label: getTranslation("general:organizations.federations.orgIdUsers"),
      },
    ],
    settings: {
      sortEnabled: true,
      sortVisible: true,
      userOptionsStorage,
      filters: {
        textFieldEnabled: true,
        textFieldFilterFields: ["name"],
        initialTextFieldFilter: userOptionsStorage.retrieveFilterText(),
      },
      labelSortBy: getTranslation("general:common.sortBy"),
      labelSearch: getTranslation("general:common.filterBy"),
    },

    pkFunction: (dta) => dta.domain,
  };

  if (
    systemRightToEdit &&
    securityModule.userHasPermission(systemRightToEdit)
  ) {
    tableConfig.fields.push({
      name: "domain",
      label: getTranslation("general:common.edit"),
      type: "icon",
      icon: "pencil",
      fixedWidth: "80px",
      notSortable: true,
      onClick: (dta) => {
        onEditSaveFn(dta.domain, dta);
      },
    });

    tableConfig.fields.push({
      name: "domain",
      label: getTranslation("general:common.delete"),
      type: "icon",
      icon: "trash",
      iconVariant: "danger",
      fixedWidth: "80px",
      notSortable: true,
      onClick: (dta) => {
        onDeleteFn?.(dta.domain, dta, "domain");
      },
    });
  }

  return tableConfig;

  function returnListOfOrganizationsUsingDomain(
    domain: string,
    orgIdOwner: string,
  ): string[] {
    const orgNames = domainsConsumers[domain]?.map((orgId) => {
      const org = allOrgsList.find((org) => org.organizationId === orgId);
      return org
        ? org.organizationId === orgIdOwner
          ? `<u>${org.name}</u>`
          : org.name
        : "";
    });

    return orgNames?.sort() || [];
  }
}

function createParentOrgEdit(
  submitButton: SlButton,
  prevData?: IDomainFederation,
  allOrgsList: IOrganization[] = [],
  domainsConsumers: Record<string, string[]> = {},
): IFormReturn<IDomainFederation> {
  const holder = document.createElement("div");

  if (!prevData) {
    throw new Error("prevData is undefined");
  }

  const formFields: IFields<IDomainFederation> = [
    {
      name: "domain",
      label: "general:domains.domain",
      type: "textBox",
      disabled: true,
      initialValue: prevData?.domain || "",
    },
    {
      name: "orgIdOwner",
      label: "general:organizations.federations.orgIdOwner",
      type: "select",
      options: allOrgsList
        .filter((org) =>
          domainsConsumers[prevData.domain].includes(org.organizationId),
        )
        .map((org) => ({
          label: org.name,
          value: org.organizationId,
        })),
      initialValue: prevData?.orgIdOwner ?? "",
    },
    {
      name: "enabled",
      label: "general:organizations.federations.enabled",
      type: "checkbox",
      disabled: true,
      initialValue: prevData?.enabled ?? true,
      helpText: "general:organizations.federations.enabledInfo",
    },
  ];

  const formValidator = z.object({
    orgIdOwner: z.string().min(1),
  }) as unknown as z.Schema<IDomainFederation>;

  const tedivoForm = new TedivoForm<IDomainFederation>({
    fields: formFields,
    onSubmit: () => undefined,
    formValidator: formValidator,
    submitButton: submitButton,
    formProps: { autoFocusOnFirstInput: true },
    hiddenData: prevData,
    logValidationResult: true,
  });

  translateTedivoForm<IDomainFederation>({
    tedivoForm,
    getTranslation,
  });

  holder.appendChild(tedivoForm.form);

  return {
    node: holder,
    tedivoForm,
    submitFunction: submitPassedToEditDrawer,
  };

  async function submitPassedToEditDrawer() {
    if (!prevData) {
      throw new Error("prevData is undefined");
    }

    const validResult = tedivoForm.doSubmitForm();
    const values = tedivoForm.getValues();
    const data: IDomainFederation = {
      ...prevData,
      orgIdOwner: values.orgIdOwner,
    };

    if (values.orgIdOwner === prevData.orgIdOwner) {
      return true;
    }

    if (validResult.success) {
      await Services.domainFederations.update(data);
      return true;
    } else {
      return false;
    }
  }
}
